<template>
  <div>
    <v-row align="end" class="mb-4">
      <v-col cols="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
    </v-row>
    <v-row justify="center" class="py-15" no-gutters v-if="loading">
      <v-col cols="auto">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>
    <template v-else>
      <v-data-table
        :headers="headers"
        :items="customSearch(filteredCompanies, headers, search)"
        hide-default-footer
        disable-pagination
        class="striped-table ordera-box-shadow"
      >
        <template v-slot:no-data>
          <v-container class="text-center py-12">
            <v-icon size="64" color="grey lighten-1" class="mb-4"> mdi-food-off </v-icon>
            <div class="text-h6 grey--text text--darken-1 mb-2">
              {{ $t("no_taken_company_meals_data") }}
            </div>
            <div class="text-body-2 grey--text">
              {{ $t("select_date_and_filters") }}
            </div>
          </v-container>
        </template>
        <template v-slot:item.num="{ item, index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.shift1="{ item }">
          {{ formatShiftDisplay(item.shift1) }}
        </template>
        <template v-slot:item.shift2="{ item }">
          {{ formatShiftDisplay(item.shift2) }}
        </template>
        <template v-slot:item.shift3="{ item }">
          {{ formatShiftDisplay(item.shift3) }}
        </template>
        <template v-slot:item.shift4="{ item }">
          {{ formatShiftDisplay(item.shift4) }}
        </template>
        <template v-slot:item.total="{ item }">
          {{ formatTotalDisplay(item) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <circle-button
            icon="mdi-format-list-bulleted"
            color="primary"
            @click="openDialogReportCompany(item)"
            :tooltip="$t('tooltip_monthly_report')"
          />
        </template>
        <template v-slot:body.append>
          <tr class="font-weight-bold">
            <td></td>
            <td>{{ $t("total") }}</td>
            <td v-if="selectedShifts.includes(1)">
              {{ formatShiftDisplay(totals.shift1) }}
            </td>
            <td v-if="selectedShifts.includes(2)">
              {{ formatShiftDisplay(totals.shift2) }}
            </td>
            <td v-if="selectedShifts.includes(3)">
              {{ formatShiftDisplay(totals.shift3) }}
            </td>
            <td v-if="selectedShifts.includes(4)">
              {{ formatShiftDisplay(totals.shift4) }}
            </td>
            <td>{{ formatShiftDisplay(totals.total) }}</td>
            <td></td>
          </tr>
        </template>
      </v-data-table>
    </template>
    <report-company-meals-taken-monthly-orders
      v-model="dialogCompany"
      :company-data="selectedCompanyItem"
    ></report-company-meals-taken-monthly-orders>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import ReportCompanyMealsTakenMonthlyOrders from "@/components/report/meals/dialogs/ReportCompanyMealsTakenMonthlyOrders.vue";

export default {
  name: "CompanyReportTabTakenCompany",
  mixins: [hasPrinting, hasExcel, hasReportPrinting],
  components: {
    ReportCompanyMealsTakenMonthlyOrders,
  },
  data() {
    return {
      report: null,
      loading: false,
      selectedShifts: [1, 2, 3, 4], // Po default sve četiri smene
      dialogCompany: false,
      selectedCompanyItem: null,
    };
  },
  created() {
    this.fetchMeals();
  },
  methods: {
    fetchMeals() {
      this.loading = true;
      reportService
        .getMealsForRestaurant(this.restaurantId, {
          company: this.selectedCompanies,
          date: this.dates.from,
          date2: this.dates.to,
        })
        .then((response) => {
          this.report = response.data;
        })
        .catch((error) => {
          console.error("Error fetching meals:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatShiftDisplay(shiftData) {
      if (!shiftData) return "0";
      const quantity = shiftData.quantity || 0;
      const quantityPayed = shiftData.quantity_payed || 0;

      if (this.canCheckReport) {
        return `${quantity}/${quantityPayed}`;
      } else {
        return `${quantity}`;
      }
    },
    formatTotalDisplay(item) {
      const total = item.total || 0;
      const totalPayed = item.total_payed || 0;

      if (this.canCheckReport) {
        return `${totalPayed}/${total}`;
      } else {
        return `${total}`;
      }
    },
    createTotalsRow() {
      return {
        num: "",
        name: this.$t("total"),
        shift1: this.formatShiftDisplay(this.totals.shift1),
        shift2: this.formatShiftDisplay(this.totals.shift2),
        shift3: this.formatShiftDisplay(this.totals.shift3),
        shift4: this.formatShiftDisplay(this.totals.shift4),
        total: this.formatShiftDisplay(this.totals.total),
      };
    },
    openDialogReportCompany(item) {
      this.dialogCompany = true;
      this.selectedCompanyItem = item;
    },
  },
  mounted() {
    this.fetchMeals();
  },
  computed: {
    companies() {
      if (!this.report) return [];

      const companiesData = [];

      for (const companyName in this.report) {
        const shifts = this.report[companyName];
        const companyData = {
          name: this.toLatin(companyName),
          shift1: { quantity: 0, quantity_payed: 0 },
          shift2: { quantity: 0, quantity_payed: 0 },
          shift3: { quantity: 0, quantity_payed: 0 },
          shift4: { quantity: 0, quantity_payed: 0 },
          total: 0,
          total_payed: 0,
        };

        for (const shiftName in shifts) {
          const mealsInShift = shifts[shiftName];
          const shiftNumber = parseInt(shiftName, 10);
          const shiftKey = `shift${shiftNumber}`;

          for (const mealName in mealsInShift) {
            const mealData = mealsInShift[mealName];

            if (companyData[shiftKey]) {
              companyData[shiftKey].quantity += mealData.quantity;
              companyData[shiftKey].quantity_payed += mealData.quantity_payed;
            }

            companyData.total += mealData.quantity;
            companyData.total_payed += mealData.quantity_payed;
          }
        }

        companiesData.push(companyData);
      }

      return companiesData;
    },
    filteredCompanies() {
      if (!this.selectedShifts || this.selectedShifts.length === 0) {
        return this.companies;
      }

      return this.companies.map((company) => {
        const filteredCompany = { ...company, total: 0, total_payed: 0 };

        // Reset shift values based on selected shifts
        filteredCompany.shift1 = this.selectedShifts.includes(1)
          ? company.shift1
          : { quantity: 0, quantity_payed: 0 };
        filteredCompany.shift2 = this.selectedShifts.includes(2)
          ? company.shift2
          : { quantity: 0, quantity_payed: 0 };
        filteredCompany.shift3 = this.selectedShifts.includes(3)
          ? company.shift3
          : { quantity: 0, quantity_payed: 0 };
        filteredCompany.shift4 = this.selectedShifts.includes(4)
          ? company.shift4
          : { quantity: 0, quantity_payed: 0 };

        // Recalculate total
        filteredCompany.total =
          filteredCompany.shift1.quantity +
          filteredCompany.shift2.quantity +
          filteredCompany.shift3.quantity +
          filteredCompany.shift4.quantity;

        filteredCompany.total_payed =
          filteredCompany.shift1.quantity_payed +
          filteredCompany.shift2.quantity_payed +
          filteredCompany.shift3.quantity_payed +
          filteredCompany.shift4.quantity_payed;

        return filteredCompany;
      });
    },
    totals() {
      const totals = {
        shift1: { quantity: 0, quantity_payed: 0 },
        shift2: { quantity: 0, quantity_payed: 0 },
        shift3: { quantity: 0, quantity_payed: 0 },
        shift4: { quantity: 0, quantity_payed: 0 },
        total: { quantity: 0, quantity_payed: 0 },
      };

      this.filteredCompanies.forEach((company) => {
        if (company.shift1) {
          totals.shift1.quantity += company.shift1.quantity;
          totals.shift1.quantity_payed += company.shift1.quantity_payed;
        }
        if (company.shift2) {
          totals.shift2.quantity += company.shift2.quantity;
          totals.shift2.quantity_payed += company.shift2.quantity_payed;
        }
        if (company.shift3) {
          totals.shift3.quantity += company.shift3.quantity;
          totals.shift3.quantity_payed += company.shift3.quantity_payed;
        }
        if (company.shift4) {
          totals.shift4.quantity += company.shift4.quantity;
          totals.shift4.quantity_payed += company.shift4.quantity_payed;
        }
        totals.total.quantity += company.total;
        totals.total.quantity_payed += company.total_payed;
      });

      return totals;
    },
    search() {
      return this.$store.state.report.filters.search;
    },
    canCheckReport() {
      const company = this.$store.state.user.user.companies[0];
      if (!company) return false;
      return !!company.checkReport;
    },
    selectedCompanies() {
      return this.$store.state.report.selectedFoodCompanies
        .map((company) => company.id)
        .join(",");
    },
    dates() {
      return this.$store.state.report.filters.dates;
    },
    restaurantId() {
      return this.$store.state.company?.restaurant_id || this.$store.state.company;
    },
    headers() {
      const baseHeaders = [
        { text: this.$t("r_b"), value: "num", print: false },
        { text: this.$t("company"), value: "name" },
      ];

      const shiftHeaders = [];

      // Dodaj header kolone samo za selektovane smene
      if (this.selectedShifts.includes(1)) {
        shiftHeaders.push({ text: this.$t("shift_one"), value: "shift1" });
      }
      if (this.selectedShifts.includes(2)) {
        shiftHeaders.push({ text: this.$t("shift_two"), value: "shift2" });
      }
      if (this.selectedShifts.includes(3)) {
        shiftHeaders.push({ text: this.$t("shift_three"), value: "shift3" });
      }
      if (this.selectedShifts.includes(4)) {
        shiftHeaders.push({ text: this.$t("shift_four"), value: "shift4" });
      }

      const totalHeader = { text: this.$t("total"), value: "total" };
      const actionsHeader = {
        text: this.$t("months"),
        value: "actions",
        sortable: false,
        print: false,
      };

      return [...baseHeaders, ...shiftHeaders, totalHeader, actionsHeader];
    },
  },
  watch: {
    "dates.from"(newVal, oldVal) {
      this.fetchMeals();
    },
    selectedCompanies(newVal, oldVal) {
      this.fetchMeals();
    },
    isPrinting(newVal, oldVal) {
      if (newVal) {
        const itemsWithTotals = [
          ...this.customSearch(this.filteredCompanies, this.headers, this.search).map(
            (company) => ({
              ...company,
              total: this.formatTotalDisplay(company),
              shift1: this.formatShiftDisplay(company.shift1),
              shift2: this.formatShiftDisplay(company.shift2),
              shift3: this.formatShiftDisplay(company.shift3),
              shift4: this.formatShiftDisplay(company.shift4),
            })
          ),
          this.createTotalsRow(),
        ];
        this.printDocument({
          tables: [
            {
              headers: this.headers.filter((h) => h.print !== false),
              items: itemsWithTotals,
              showCaption: true,
            },
          ],
          title: [this.$t("report"), this.$t("taken_meals_by_companies")].join(": "),
          subtitle: this.subtitle,
          hideDatetime: true,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        const itemsForExcel = [
          ...this.customSearch(this.filteredCompanies, this.headers, this.search).map(
            (company) => ({
              ...company,
              total: this.formatTotalDisplay(company),
              shift1: this.formatShiftDisplay(company.shift1),
              shift2: this.formatShiftDisplay(company.shift2),
              shift3: this.formatShiftDisplay(company.shift3),
              shift4: this.formatShiftDisplay(company.shift4),
            })
          ),
          this.createTotalsRow(),
        ];
        this.downloadExcel({
          fileName:
            [this.$t("report"), this.$t("taken_meals_by_companies")].join(" ") + ".xlsx",
          sheets: [
            {
              name: [this.$t("report"), this.$t("taken_meals_by_companies")].join(" "),
              headers: this.headers.filter((h) => h.print !== false),
              items: itemsForExcel,
              title: [this.$t("report"), this.$t("taken_meals_by_companies")].join(" "),
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
  },
};
</script>
